import { Table } from 'components/core-ui/Table'
import s from './DailyReports.modules.scss'
import { useEffect, useMemo, useState } from 'react'
import { DailyReportTableItem } from '../interfaces'
import { makeCoreGetRequest, makeCorePatchRequest, makeCorePutRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification, usePermissions } from 'hooks'
import { getColumns, getFilters } from './helpers'
import { dailyReportsRoles } from '../constants'
import {
  AddActionIcon,
  DeleteActionIcon,
  DownloadActionIcon,
  EditActionIcon,
  PrintActionIcon,
  UploadActionIcon
} from 'icons'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { Typography } from '@mui/material'
import { DailyReportModal } from './DailyReportModal'
import { downloadDocument, getWasteCategoryOptions } from '../api'
import { UploadFilesModal } from 'components/core-ui/UploadFilesModal'
import { handleQueryError } from 'utils/helpers'
import useUpload from '../common/hooks/useUpload'
import { EmojiFlagsOutlined } from '@mui/icons-material'
import { SignReportConfirmationModal } from './SignReportConfirmationModal'
import { useQueryClient } from 'react-query'
import { PrintPreviewModal } from '../common/PrintPreviewModal'

const DailyReports = () => {
  const [tableItems, setTableItems] = useState<DailyReportTableItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isUploadFilesModalOpen, setIsUploadFilesModalOpen] = useState<boolean>(false)
  const [isSignModalOpen, setIsSignModalOpen] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<DailyReportTableItem>()
  const [shouldRefetchItem, setShouldRefetchItem] = useState(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>(0)
  const [isPrintPreviewModalOpen, setIsPrintPreviewModalOpen] = useState<boolean>(false)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isPdfBlobLoading, setIsPdfBlobLoading] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const queryClient = useQueryClient()

  const { canCreate, canEdit, canDelete } = usePermissions(dailyReportsRoles)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => {
    setIsEditModalOpen(true)
    setShouldRefetchItem(true)
  }

  const closeEditModal = (): void => {
    setIsEditModalOpen(false)
    setShouldRefetchItem(false)
  }

  const openSignModal = (): void => setIsSignModalOpen(true)
  const closeSignModal = (): void => setIsSignModalOpen(false)

  const openUploadFilesModal = (): void => setIsUploadFilesModalOpen(true)
  const closeUploadFilesModal = (): void => setIsUploadFilesModalOpen(false)

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const toolbarActions = useMemo(() => {
    if (canCreate) {
      return [
        {
          label: 'Adauga',
          icon: <AddActionIcon />,
          onClick: () => openNewModal()
        }
      ]
    }

    return []
  }, [canCreate])

  const { data: wasteCategoryData } = getWasteCategoryOptions(showNotification)

  const wasteCategoryOptions = useMemo(
    () => wasteCategoryData?.map(option => ({ label: option.label, value: option.code })),
    [wasteCategoryData]
  )

  const downloadQuery = downloadDocument(
    showNotification,
    'DAILY',
    rowDownloadId,
    'DAILY_REPORT',
    () => setRowDownloadId(0)
  )

  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(() => getFilters(wasteCategoryOptions), [wasteCategoryOptions])

  const selectRow = (rowId: number, action: string) => {
    const item = tableItems.find(item => item.id === rowId)
    setSelectedRow(item)

    switch (action) {
      case 'edit':
        openEditModal()
        break
      case 'upload':
        openUploadFilesModal()
        break
      case 'delete':
        openDeleteModal()
        break
      case 'sign':
        openSignModal()
        break
      default:
        break
    }
  }

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/daily-report',
          filters: [{ name: 'currentLocation', value: true }],
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru rapoartele zilnice. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const { saveDocuments } = useUpload({
    reportId: selectedRow?.id,
    reportType: 'DAILY',
    onSuccess: () => {
      setShouldRefreshData(true)
      closeUploadFilesModal()
      showNotification({
        message: `Fisierele au fost incarcate cu success.`,
        type: 'success'
      })
    },
    onError: error => {
      handleQueryError(error, `A aparut o eroare la incarcarea fisierelor.`, showNotification)
    }
  })

  const openPrintPreviewModal = (): void => setIsPrintPreviewModalOpen(true)
  const closePrintPreviewModal = (): void => {
    setIsPrintPreviewModalOpen(false)
    setPdfBlob(null)
  }

  const printPDF = async (rowId: number) => {
    try {
      setIsPdfBlobLoading(true)
      openPrintPreviewModal()

      const response = await makeCoreGetRequest(
        `/waste-management-system/daily-report/pdf/${rowId}`,
        {
          responseType: 'blob'
        }
      )

      const blob = new Blob([response], { type: 'application/pdf' })
      setPdfBlob(blob)
    } catch (error) {
      closePrintPreviewModal()
      showNotification({
        message: `A aparut o eroare la generarea raportului PDF. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsPdfBlobLoading(false)
    }
  }

  const handleSignReport = async () => {
    try {
      await makeCorePatchRequest(`/waste-management-system/daily-report/${selectedRow?.id}`)
      closeSignModal()
      showNotification({
        message: 'Raportul a fost semnat cu succes.',
        type: 'success'
      })
      setShouldRefreshData(true)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la semnarea raportului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const refreshDataAfterDelete = () => {
    setShouldRefreshData(true)
    queryClient.invalidateQueries(['filtered-waste-categories'])
    queryClient.invalidateQueries(['current-collecting-companies'])
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <DailyReportModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <DailyReportModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={selectedRow?.id}
          shouldRefetchItem={shouldRefetchItem}
          setShouldRefreshData={setShouldRefreshData}
          isEdit
        />
      )}
      {canEdit && (
        <UploadFilesModal
          isModalOpen={isUploadFilesModalOpen}
          closeModal={closeUploadFilesModal}
          saveDocuments={saveDocuments}
          documentType={{ value: 'DAILY_REPORT', label: 'Raport Zilnic' }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      )}
      {canDelete && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          actionUrl="/waste-management-system/daily-report/:id"
          itemId={`${selectedRow?.id}`}
          content={<Typography>Sunteti sigur ca vreti sa stergeti acest raport?</Typography>}
          additionalAction={refreshDataAfterDelete}
        />
      )}
      <SignReportConfirmationModal
        isModalOpen={isSignModalOpen}
        closeModal={closeSignModal}
        onSign={handleSignReport}
      />
      <PrintPreviewModal
        isModalOpen={isPrintPreviewModalOpen}
        closeModal={closePrintPreviewModal}
        pdfBlob={pdfBlob}
        isPdfBlobLoading={isPdfBlobLoading}
      />
      <Table
        hideCheckbox
        orderBy="creationDate"
        order="desc"
        columns={columns}
        items={tableItems}
        rowColorRules={[{ type: 'eq', field: 'isReportSigned', value: false, color: '#FEF8C6' }]}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Print',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#4A148C',
            desktopIcon: <PrintActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => printPDF(rowId),
            hideCondition: { field: 'isCompanyReport', value: null },
            showTooltip: true
          },
          canEdit && {
            label: 'Upload',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#FFB300',
            desktopIcon: <UploadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'upload'),
            hideCondition: { field: 'reportUploadStatus', value: 'AUTOMATIC_UPLOAD' },
            showTooltip: true
          },
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#33691E',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => setRowDownloadId(rowId),
            hideCondition: { field: 'documentNo', value: 0 },
            showTooltip: true
          },
          canEdit && {
            label: 'Editeaza/Semneaza',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#0970E6',
            desktopIcon: <EditActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'edit'),
            hideCondition: { field: 'isReportSigned', value: true },
            showTooltip: true
          },
          canDelete && {
            label: 'Sterge',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'delete'),
            hideCondition: { field: 'isReportSigned', value: true },
            showTooltip: true
          },
          canEdit && {
            label: 'Confirma incarcare document',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#0970E6',
            desktopIcon: (
              <EmojiFlagsOutlined style={{ color: '#0970E6', width: '20px', height: '20px' }} />
            ),
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'sign'),
            hideCondition: { field: 'showFlagIcon', value: false },
            showTooltip: true
          }
        ]}
        rowActionsWidth="350px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default DailyReports
